var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { assertIsRangedControl, } from './Control';
import { volumeRanges } from './DspConfig';
import { assertIsQueryParam } from './EquipmentServiceDriver';
import { convertNumber, handleTransportError } from './ParamBridge';
import { ValueBridge } from './ValueBridge';
// The VolumeBridge automatically handles synchronization between volume and volume mute. There are
// three cases.
//
// 1. The device does not support volume mute. In this case muteControl will be undefined.
// 2. The device has an API to get/set the volume mute independently from the volume. In this case,
//    muteControl will be a Control<boolean>. Whenever the volume is set, the volume mute will be
//    set if necessary to make sure that it is muted if the volume is at its minimum value (as
//    defined by control.range.min) and unmuted if it is above its minimum value.
// 3. The device does not have a volume mute API, but it automatically mutes its output when its
//    volume is at its minimum value. This minimum value may be lower than the minimum in the range
//    specified by the volume control if less than the full range is being used. In this case,
//    muteControl will be a VolumeMuteSyncControl. Whenever the volume state is updated, the
//    volume mute will be evaluated and the volume mute state will be updated.
export class VolumeBridge extends ValueBridge {
    constructor(config, state, range, muteState, control, muteControl, controlInfo, powerState, transport, valueCallback, log) {
        super('volume', state, control, controlInfo, powerState, transport, valueCallback, log);
        this.config = config;
        this.range = range;
        this.muteState = muteState;
        this.muteControl = muteControl;
        if (this.muteControl !== undefined && 'min' in this.muteControl) {
            assertIsRangedControl(this.control);
            this.muteState.update(this.state.value <= this.range.value.min);
            this.muteStateSubscription = this.muteState.subscribe((prevValue) => {
                this.valueCallback('volumeMute', this.muteState.value);
            });
            this.queryHandlers['volumeMute'] = (param) => {
                assertIsQueryParam(param);
                this.valueCallback(param, this.muteState.value);
            };
            this.secondStateSubscription = this.state.subscribe((prevValue) => {
                assertIsRangedControl(this.control);
                this.muteState.update(this.state.value <= this.range.value.min);
            });
            this.volumeRangeStateSubscription = this.range.subscribe((prevValue) => {
                const prevVolume = prevValue.min === 0 && prevValue.max === 0
                    ? this.state.value
                    : convertNumber(this.state.value, prevValue.max, prevValue.min, volumeRanges.volume.max, volumeRanges.volume.min, 1);
                const newVolume = this.range.value.min === 0 && this.range.value.max === 0
                    ? this.state.value
                    : convertNumber(this.state.value, this.range.value.max, this.range.value.min, volumeRanges.volume.max, volumeRanges.volume.min, 1);
                if (newVolume !== prevVolume) {
                    this.valueCallback('volume', newVolume);
                }
            });
        }
    }
    destroy() {
        if (this.muteStateSubscription !== undefined) {
            this.muteState.unsubscribe(this.muteStateSubscription);
        }
        if (this.secondStateSubscription !== undefined) {
            this.state.unsubscribe(this.secondStateSubscription);
        }
        if (this.volumeRangeStateSubscription !== undefined) {
            this.range.unsubscribe(this.volumeRangeStateSubscription);
        }
        super.destroy();
    }
    onSet(param, setValue) {
        const _super = Object.create(null, {
            onSet: { get: () => super.onSet }
        });
        return __awaiter(this, void 0, void 0, function* () {
            assertIsRangedControl(this.control);
            yield _super.onSet.call(this, param, setValue);
            if (!this.controlInfo.canSet(this.powerState.value))
                return; // super handles response
            if (this.muteControl !== undefined &&
                'set' in this.muteControl &&
                !this.controlInfo.isToggle) {
                const newMute = this.state.value <= this.range.value.min;
                if (newMute !== this.muteState.value) {
                    const tags = ['poll', 'set.volumeMute'];
                    this.transport.removeCommands(tags);
                    try {
                        const newValue = yield this.muteControl.set(newMute, ['set.volumeMute']);
                        this.muteState.update(newValue);
                    }
                    catch (error) {
                        handleTransportError(this.log, error);
                    }
                }
            }
        });
    }
    convertFromParam(value) {
        assertIsRangedControl(this.control);
        if (this.muteControl !== undefined && 'min' in this.muteControl && value === 0) {
            return this.muteControl.min;
        }
        if (this.range.value.min === 0 && this.range.value.max === 0)
            return value;
        return convertNumber(value, volumeRanges.volume.max, volumeRanges.volume.min, this.range.value.max, this.range.value.min, this.range.value.round);
    }
    convertToParam(value) {
        assertIsRangedControl(this.control);
        if (this.range.value.min === 0 && this.range.value.max === 0)
            return value;
        return convertNumber(value, this.range.value.max, this.range.value.min, volumeRanges.volume.max, volumeRanges.volume.min, 1);
    }
    validate() {
        return __awaiter(this, void 0, void 0, function* () {
            // This control doesn't validate anything, but we use this opportunity to query the range.
            const rc = this.control;
            this.range.update(typeof rc.range === 'function' ? yield rc.range(this.config) : rc.range);
            return undefined;
        });
    }
}
